import { useEffect, useState } from 'react'
import { Box } from '@mui/system'
import { Divider, Modal, Button, Typography } from 'antd'
import { Task } from '../../../models/models'
import { toastFailure } from '../../../util/toast'
import { CircularProgress, styled } from '@mui/material'
import '../../tables/table-styles.css'
import { deleteTasksRequest, getTaskByIdRequest, TaskRelation } from '../../../api/tasks'
import { OrderedListOutlined } from '@ant-design/icons'

const Header = styled('div')`
    gap: 10px;
    align-items: center;
    display: flex;
`

const Title = styled('p')`
    font-size: 20px;
    margin: 0;
`

interface UpdateTaskModalProps {
    taskId: number
    closeModal: (task?: Task) => void
}

enum ModalState {
    NotFound = 'NotFound',
    Error = 'Error',
    Success = 'Success',
    Loading = 'Loading',
}

export default function UpdateTaskModal({ taskId, closeModal }: UpdateTaskModalProps) {
    const [modalState, setModalState] = useState<ModalState>(ModalState.Loading)
    const [task, setTask] = useState<Task>()

    useEffect(() => {
        getTask()
    }, [])

    const getTask = async () => {
        const response = await getTaskByIdRequest(taskId, {
            relations: [TaskRelation.AssignedTo, TaskRelation.CreatedBy, TaskRelation.Type],
        })

        if (response.successful) {
            const task = response.data
            setTask(task)
            setModalState(ModalState.Success)
        } else {
            if (response.status === 404) {
                setModalState(ModalState.NotFound)
            } else {
                setModalState(ModalState.Error)
            }
            toastFailure(response.message)
        }
    }

    const deleteTask = async () => {
        const response = await deleteTasksRequest(taskId)
        if (response.successful) {
            closeModal(response.data)
            return
        }
        toastFailure(`Something went wrong deleting task ${taskId}`)
    }

    return (
        <Modal visible={true} footer={null} onCancel={() => closeModal()}>
            {modalState === ModalState.Loading && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                        margin: '20px',
                        width: '100%',
                        height: '500px',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <CircularProgress size={75} style={{ color: 'rgb(53, 0, 160)' }} />
                </Box>
            )}
            {modalState === ModalState.Error && <Box>Something went wrong</Box>}
            {modalState === ModalState.NotFound && (
                <Box>Could not find the task with the id of {taskId}</Box>
            )}
            {modalState === ModalState.Success && (
                <Box>
                    <Header>
                        <OrderedListOutlined style={{ fontSize: '20px' }} />
                        <Title>Delete task {taskId}</Title>
                    </Header>
                    <Divider />

                    <Typography.Text type="danger">
                        Are you sure you want to delete this task? Its time allocation might be
                        subtracted from the production-schedule.
                    </Typography.Text>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', margin: '15px' }}>
                        <Button type="primary" danger onClick={deleteTask}>
                            Delete
                        </Button>
                    </Box>
                </Box>
            )}
        </Modal>
    )
}
